import axios from "axios";
import { useEffect, useState } from "react";
import { safeGet } from "../../../../utils/safeStorage";

// The AI service (e1xacademy-ai). It checks the same login token as the main API.
export const AI_URL = (process.env.REACT_APP_AI_URL || "").replace(/\/$/, "");
export const aiEnabled = Boolean(AI_URL);

const token = () => safeGet("nd-rest-tkn");
const ai = axios.create({ baseURL: AI_URL, timeout: 30000 });
ai.interceptors.request.use((cfg) => {
  const t = token();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Whether this user may use the AI (while testing, only students an admin switched on). Asked once per page load.
let accessCheck = null;
const checkAccess = () => {
  if (!aiEnabled || !token()) return Promise.resolve(false);
  accessCheck ??= ai.get("/access").then((r) => r.data.enabled === true).catch(() => { accessCheck = null; return false; });
  return accessCheck;
};

/** null while checking, then true or false. */
export function useAiAccess() {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let live = true;
    checkAccess().then((ok) => { if (live) setAccess(ok); });
    return () => { live = false; };
  }, []);
  return access;
}

export const aiError = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

export const listChats = (kind, course) => ai.get("/conversations", { params: { kind, course } }).then((r) => r.data);
export const createChat = (kind, course) => ai.post("/conversations", { kind, course }).then((r) => r.data.conversation);
export const chatMessages = (id) => ai.get(`/conversations/${id}/messages`).then((r) => r.data.messages);
export const deleteChat = (id) => ai.delete(`/conversations/${id}`);
export const rateMessage = (id, messageId, rating) => ai.post(`/conversations/${id}/messages/${messageId}/rating`, { rating });

/**
 * Send a message and read the streamed reply (server-sent events over fetch, since EventSource
 * can't POST or send the token). on: { text(t), tool(label), done(messageId), error(message) }.
 */
export async function sendMessage(id, text, on, signal) {
  const res = await fetch(`${AI_URL}/conversations/${id}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ text }),
    signal,
  });
  if (!res.ok || !res.body) {
    let message = "The AI couldn't reply. Try again.";
    try { message = (await res.json()).message || message; } catch { /* not JSON */ }
    on.error(message);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let cut;
    while ((cut = buffer.indexOf("\n\n")) >= 0) {
      const chunk = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const event = /^event: (.+)$/m.exec(chunk)?.[1];
      const data = /^data: (.+)$/m.exec(chunk)?.[1];
      if (!event || !data) continue;
      const payload = JSON.parse(data);
      if (event === "text") on.text(payload.text);
      else if (event === "tool") on.tool(payload.label);
      else if (event === "done") { finished = true; on.done(payload.messageId); }
      else if (event === "error") { finished = true; on.error(payload.message); }
    }
  }
  if (!finished) on.error("The reply was cut off. Try again.");
}
